import { useCallback, useEffect, useState } from 'react';
import { fetchLeadsInventory, fetchLeadTrace } from '../../../services/analytics-api.js';
import { getIdToken } from '../../../services/firebase-auth.js';
import KommoExportPanel from './KommoExportPanel.jsx';

function when(value) {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString('es-CL', { timeZone: 'America/Santiago' });
}

export default function LeadsTracePage() {
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('');
  const [offset, setOffset] = useState(0);
  const [result, setResult] = useState(null);
  const [trace, setTrace] = useState(null);
  const [busy, setBusy] = useState(false);
  const [traceBusy, setTraceBusy] = useState(false);
  const [error, setError] = useState('');
  const [traceError, setTraceError] = useState('');

  const load = useCallback(async () => {
    setBusy(true); setError('');
    try {
      const token = await getIdToken(false);
      const data = await fetchLeadsInventory({
        dateFrom: '2000-01-01', dateTo: '2100-01-01',
        q: filter, limit: 100, offset
      }, token);
      if (!data || data.success === false) throw new Error(data?.error || 'No se pudo consultar el inventario');
      setResult(data);
    } catch (e) {
      setError(e.message); setResult(null);
    } finally { setBusy(false); }
  }, [filter, offset]);

  useEffect(() => { load(); }, [load]);

  async function inspect(id) {
    setTrace(null); setTraceError(''); setTraceBusy(true);
    try {
      const token = await getIdToken(false);
      const data = await fetchLeadTrace(id, token);
      if (!data || data.success === false) throw new Error(data?.error || 'No se pudo consultar el historial');
      setTrace(data);
    } catch (e) { setTraceError(e.message); }
    finally { setTraceBusy(false); }
  }

  const rows = result?.rows || [];
  const total = result?.totals?.matched || 0;
  return (
    <section className="analytics-section">
      <h1>Trazabilidad de leads Kommo</h1>
      <KommoExportPanel />
      <p>Origen, ingreso y estado provienen del espejo de Kommo. El historial se consulta directamente en Kommo al seleccionar un lead. No representa la fecha en que se realizó la reunión.</p>
      <p>La cobertura inicial puede estar limitada por la sincronización del espejo (aproximadamente 2.000 leads). La consulta histórica completa todavía requiere validar paginación y disponibilidad de eventos antiguos.</p>
      <form onSubmit={e => { e.preventDefault(); setOffset(0); setFilter(search.trim()); }}>
        <input className="analytics-input" aria-label="Buscar lead" placeholder="Buscar nombre o ID de lead" value={search} onChange={e => setSearch(e.target.value)} />
        <button className="analytics-btn" type="submit">Buscar</button>
        <button className="analytics-btn analytics-btn-ghost" type="button" onClick={load}>Actualizar</button>
      </form>
      {error && <p className="analytics-error-inline" role="alert">{error}</p>}
      {busy ? <p>Cargando leads…</p> : <>
        <p>Leads {rows.length ? offset + 1 : 0}–{offset + rows.length} de {total}</p>
        <div style={{overflowX:'auto'}}>
          <table className="analytics-table">
            <thead><tr><th>Lead</th><th>Origen</th><th>Fecha de ingreso</th><th>Estado actual</th><th>Historial</th></tr></thead>
            <tbody>{rows.map(lead => <tr key={lead.leadId}>
              <td>{lead.name || 'Sin nombre'} ({lead.leadId})</td>
              <td>{lead.originTag || lead.origenMeta || 'Sin clasificar'}</td>
              <td>{when(lead.createdAt)}</td>
              <td>{[lead.pipelineName,lead.stageName].filter(Boolean).join(' / ') || 'Sin clasificar'}</td>
              <td><button className="analytics-btn" disabled={traceBusy} onClick={() => inspect(lead.leadId)}>Ver pasos por Reunión</button></td>
            </tr>)}</tbody>
          </table>
        </div>
        <div style={{display:'flex',gap:8,marginTop:12}}>
          <button className="analytics-btn" disabled={offset === 0 || busy} onClick={() => setOffset(Math.max(0,offset-100))}>Anterior</button>
          <button className="analytics-btn" disabled={offset+rows.length>=total || busy} onClick={() => setOffset(offset+100)}>Siguiente</button>
        </div>
      </>}
      {traceBusy && <p>Consultando historial directamente en Kommo…</p>}
      {traceError && <p className="analytics-error-inline" role="alert">{traceError}</p>}
      {trace && <section style={{marginTop:24}}>
        <h2>Historial del lead {trace.trace.leadId}</h2>
        <p>Primera entrada a Reunión: <strong>{when(trace.trace.firstMeetingStageAt)}</strong></p>
        <p>Última entrada a Reunión: <strong>{when(trace.trace.lastMeetingStageAt)}</strong></p>
        <p>Entradas registradas: {trace.trace.meetingStageTransitions}</p>
        {trace.trace.truncated && <p role="alert">Historial parcial: Kommo indica más páginas que las consultadas.</p>}
        {trace.note && <p>{trace.note}</p>}
        <ul>{trace.trace.events.map(event => <li key={event.eventId}>{when(event.occurredAt)}: {event.from.name || event.from.statusId || '?'} → {event.to.name || event.to.statusId || '?'}</li>)}</ul>
      </section>}
    </section>
  );
}
